import api from '../../API/baseURL';
import { handleAuthErrors } from '../../utils/vsCodeUtils';

const API_ENDPOINTS = {
    SIGNUP: '/vs-code/auth/signupV2',
    SIGNIN: '/vs-code/auth/signinV2',
    REGISTER: '/auth/register',  // New endpoint for team-aware registration
    CHECK_AUTH_PROVIDER: '/vs-code/auth/check-auth-provider',
    FORGOT_PASSWORD: '/vs-code/auth/forgot-password',
    VERIFY_EMAIL: '/vs-code/auth/verify-email',
    RESEND_VERIFICATION: '/vs-code/auth/resend-verification-otp',
    RESET_PASSWORD: '/vs-code/auth/reset-password',
    GET_USER_INFO: '/auth/get_user_info',
    SWITCH_PLAN: '/plans/switch',
    BYOK_STATUS: '/plans/byok/status',
    BYOK_SUBSCRIBE: '/plans/byok/subscribe',
    BYOK_BILLING_HISTORY: '/plans/byok/billing-history',
    BYOK_INVOICES: '/plans/byok/invoices',
    BYOK_CANCEL: '/plans/byok/cancel'
};

// Get user information using access token only
export const getUserInfoFromAuth = async () => {
    try {
        const token = localStorage.getItem('web_access_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.get(API_ENDPOINTS.GET_USER_INFO, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user info from auth:', error);
        handleAuthErrors(error);
    }
};

// Switch between plans (byok, sixth_credit, or fixed)
// target_plan: 'byok' | 'sixth_credit' | 'fixed'
// priceId: required when target_plan is 'fixed' (Stripe price ID)
export const switchPlan = async (targetPlan, priceId = null) => {
    try {
        const token = localStorage.getItem('web_access_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const payload = { target_plan: targetPlan };
        if (priceId) {
            payload.price_id = priceId;
        }

        const response = await api.post(API_ENDPOINTS.SWITCH_PLAN, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error switching plan:', error);
        handleAuthErrors(error);
        throw error;
    }
};

// Get BYOK subscription status
export const getBYOKStatus = async () => {
    try {
        const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.get(API_ENDPOINTS.BYOK_STATUS, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching BYOK status:', error);
        handleAuthErrors(error);
        throw error;
    }
};

// Subscribe to BYOK plan
// subscription_type: 'monthly' | 'yearly' | 'lifetime'
export const subscribeBYOK = async (subscriptionType) => {
    try {
        const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.post(API_ENDPOINTS.BYOK_SUBSCRIBE, {
            subscription_type: subscriptionType
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error subscribing to BYOK plan:', error);
        handleAuthErrors(error);
        throw error;
    }
};

// Get BYOK billing/payment history
export const getBYOKBillingHistory = async () => {
    try {
        const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.get(API_ENDPOINTS.BYOK_BILLING_HISTORY, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching BYOK billing history:', error);
        handleAuthErrors(error);
        throw error;
    }
};

// Get BYOK invoices
export const getBYOKInvoices = async () => {
    try {
        const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.get(API_ENDPOINTS.BYOK_INVOICES, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching BYOK invoices:', error);
        handleAuthErrors(error);
        throw error;
    }
};

// Cancel BYOK subscription
export const cancelBYOKSubscription = async (reason = '') => {
    try {
        const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
        if (!token) {
            throw new Error('No access token found');
        }

        const response = await api.post(API_ENDPOINTS.BYOK_CANCEL, {
            reason: reason
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error cancelling BYOK subscription:', error);
        handleAuthErrors(error);
        throw error;
    }
};

export default API_ENDPOINTS;
